import { z } from 'zod';

export const CorrectionFormSchema = z.object({
	name: z.string().min(1, 'Identificação é obrigatória'),
	prop: z.string().min(1, 'Propriedade é obrigatória'),
	area: z.string().min(1, 'Área é obrigatória'),
	sectors: z.array(z.string()).min(1, 'Selecione pelo menos um setor'),
	soilAnalysis: z.string().min(1, 'Análise de solo é obrigatória'),
});

const requiredPositive = (label: string) =>
	z.string().superRefine((v, ctx) => {
		if (!v) {
			ctx.addIssue({ code: 'custom', message: `${label} é obrigatório` });
		} else if (Number.isNaN(Number(v))) {
			ctx.addIssue({ code: 'custom', message: `${label} deve ser um número válido` });
		} else {
			const n = Number(v);
			if (n < 0) ctx.addIssue({ code: 'custom', message: `${label} não pode ser negativo` });
			else if (n === 0)
				ctx.addIssue({ code: 'custom', message: `${label} deve ser maior que zero` });
		}
	});

const requiredPercentage = (label: string) =>
	z.string().superRefine((v, ctx) => {
		if (!v) {
			ctx.addIssue({ code: 'custom', message: `${label} é obrigatório` });
		} else if (Number.isNaN(Number(v))) {
			ctx.addIssue({ code: 'custom', message: `${label} deve ser um número válido` });
		} else {
			const n = Number(v);
			if (n < 0) ctx.addIssue({ code: 'custom', message: `${label} não pode ser negativo` });
			else if (n === 0)
				ctx.addIssue({ code: 'custom', message: `${label} deve ser maior que zero` });
			else if (n > 100)
				ctx.addIssue({ code: 'custom', message: `${label} deve estar entre 1 e 100` });
		}
	});

const addPositiveIssue = (v: string, label: string, ctx: z.RefinementCtx, path: [string]) => {
	if (!v) {
		ctx.addIssue({ code: 'custom', message: `${label} é obrigatório`, path });
	} else if (Number.isNaN(Number(v))) {
		ctx.addIssue({ code: 'custom', message: `${label} deve ser um número válido`, path });
	} else {
		const n = Number(v);
		if (n < 0) ctx.addIssue({ code: 'custom', message: `${label} não pode ser negativo`, path });
		else if (n === 0)
			ctx.addIssue({ code: 'custom', message: `${label} deve ser maior que zero`, path });
	}
};

const addPercentageIssue = (v: string, label: string, ctx: z.RefinementCtx, path: [string]) => {
	if (!v) {
		ctx.addIssue({ code: 'custom', message: `${label} é obrigatório`, path });
	} else if (Number.isNaN(Number(v))) {
		ctx.addIssue({ code: 'custom', message: `${label} deve ser um número válido`, path });
	} else {
		const n = Number(v);
		if (n < 0) ctx.addIssue({ code: 'custom', message: `${label} não pode ser negativo`, path });
		else if (n === 0)
			ctx.addIssue({ code: 'custom', message: `${label} deve ser maior que zero`, path });
		else if (n > 100)
			ctx.addIssue({ code: 'custom', message: `${label} deve estar entre 1 e 100`, path });
	}
};

export const CalagemSchema = z
	.object({
		corretivo: z.string().min(1, 'Corretivo é obrigatório'),
		metodo: z.enum(['satSB', 'CaMg'], { error: 'Método é obrigatório' }),
		metodoValue: z.string(),
		forma: z.enum(['total', 'sulco', 'faixa', 'cova'], {
			error: 'Forma de aplicação é obrigatória',
		}),
		profundidade: requiredPositive('Profundidade'),
		largura: z.string().optional(),
		comprimento: z.string().optional(),
	})
	.superRefine((val, ctx) => {
		if (val.metodo === 'satSB') {
			addPercentageIssue(val.metodoValue, 'V desejado', ctx, ['metodoValue']);
		} else if (val.metodo === 'CaMg') {
			addPositiveIssue(val.metodoValue, 'Ca + Mg desejado', ctx, ['metodoValue']);
		}

		if (val.forma === 'sulco' || val.forma === 'faixa' || val.forma === 'cova') {
			addPositiveIssue(val.largura ?? '', 'Largura', ctx, ['largura']);
		}
		if (val.forma === 'cova') {
			addPositiveIssue(val.comprimento ?? '', 'Comprimento', ctx, ['comprimento']);
		}
	});

export const FosfatagemSchema = z
	.object({
		fertilizanteP: z.string().min(1, 'Fertilizante P é obrigatório'),
		fertilizanteComplementar: z.string().optional().default(''),
		forma: z.enum(['sulco', 'cova'], {
			error: 'Forma de aplicação é obrigatória',
		}),
		profundidade: requiredPositive('Profundidade'),
		largura: z.string().optional(),
		comprimento: z.string().optional(),
	})
	.superRefine((val, ctx) => {
		if (val.forma === 'sulco' || val.forma === 'cova') {
			addPositiveIssue(val.largura ?? '', 'Largura', ctx, ['largura']);
		}
		if (val.forma === 'cova') {
			addPositiveIssue(val.comprimento ?? '', 'Comprimento', ctx, ['comprimento']);
		}
	});

export const GessagemSchema = z
	.object({
		soilAnalysis: z.string().min(1, 'Análise de solo é obrigatória'),
		condicionador: z.string().min(1, 'Condicionador é obrigatório'),
		satCa2: requiredPercentage('Saturação por Ca'),
		forma: z.enum(['total', 'sulco', 'faixa', 'cova'], {
			error: 'Forma de aplicação é obrigatória',
		}),
		profundidade: z.string().optional(),
		largura: z.string().optional(),
		comprimento: z.string().optional(),
	})
	.superRefine((val, ctx) => {
		if (val.forma === 'sulco' || val.forma === 'cova') {
			addPositiveIssue(val.profundidade ?? '', 'Profundidade', ctx, ['profundidade']);
		}
		if (val.forma === 'sulco' || val.forma === 'faixa' || val.forma === 'cova') {
			addPositiveIssue(val.largura ?? '', 'Largura', ctx, ['largura']);
		}
		if (val.forma === 'cova') {
			addPositiveIssue(val.comprimento ?? '', 'Comprimento', ctx, ['comprimento']);
		}
	});

export const RecommendationTabSchema = z.object({
	identificacao: z.string().min(1, 'Identificação é obrigatória'),
	observacoes: z.string().optional(),
});
